import React from 'react';
import { FiClock, FiCheckCircle, FiClipboard } from 'react-icons/fi';
import styled from 'styled-components';

const Section = styled.section`
  padding: 4rem 0;
  background: ${props => props.theme.colors.background};
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 2rem;
`;

const SectionHeader = styled.div`
  text-align: center;
  margin-bottom: 3rem;
`;

const SectionTitle = styled.h2`
  font-size: ${props => props.theme.fontSizes['4xl']};
  font-weight: ${props => props.theme.fontWeights.bold};
  color: ${props => props.theme.colors.primary};
  margin-bottom: 1rem;
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    bottom: -10px;
    left: 50%;
    transform: translateX(-50%);
    width: 80px;
    height: 4px;
    background: ${props => props.theme.colors.secondary};
    border-radius: 2px;
  }
`;

const SectionSubtitle = styled.p`
  font-size: ${props => props.theme.fontSizes.lg};
  color: ${props => props.theme.colors.textSecondary};
  max-width: 600px;
  margin: 0 auto;
`;

const RequirementsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 2rem;
  
  @media (min-width: ${props => props.theme.breakpoints.md}) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (min-width: ${props => props.theme.breakpoints.lg}) {
    grid-template-columns: repeat(3, 1fr);
  }
`;

const RequirementCard = styled.div`
  background: ${props => props.theme.colors.surface};
  padding: 2rem;
  border-radius: ${props => props.theme.borderRadius.xl};
  box-shadow: ${props => props.theme.shadows.md};
  border-top: 4px solid ${props => props.theme.colors.primary};
  transition: ${props => props.theme.transitions.base};
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(90deg, ${props => props.theme.colors.primary}, ${props => props.theme.colors.secondary});
  }
  
  &:hover {
    transform: translateY(-3px);
    box-shadow: ${props => props.theme.shadows.lg};
  }
`;

const CardHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1.5rem;
`;

const CardIcon = styled.div`
  width: 50px;
  height: 50px;
  background: ${props => props.theme.colors.primary};
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: white;
  font-size: 1.5rem;
  flex-shrink: 0;
`;

const CardTitle = styled.h3`
  font-size: ${props => props.theme.fontSizes.xl};
  font-weight: ${props => props.theme.fontWeights.semibold};
  color: ${props => props.theme.colors.primary};
  margin: 0;
`;

const CardContent = styled.div`
  font-size: ${props => props.theme.fontSizes.base};
  line-height: 1.7;
  color: ${props => props.theme.colors.text};
  
  p {
    margin-bottom: 1rem;
    
    &:last-child {
      margin-bottom: 0;
    }
  }
  
  ul {
    margin: 1rem 0;
    padding-left: 1.5rem;
    
    li {
      margin-bottom: 0.5rem;
    }
  }
`;

const RequirementsSection = ({ courseDetail }) => {
  if (!courseDetail) return null;

  return (
    <Section>
      <Container>
        <SectionHeader>
          <SectionTitle>Requirements & Eligibility</SectionTitle>
          <SectionSubtitle>
            Everything you need to know about qualifying for {courseDetail.courseTitle}
          </SectionSubtitle>
        </SectionHeader>
        
        <RequirementsGrid>
          <RequirementCard>
            <CardHeader>
              <CardIcon><FiClock /></CardIcon>
              <CardTitle>Experience Requirements</CardTitle>
            </CardHeader>
            <CardContent>
              <p>{courseDetail.experienceRequirements}</p>
            </CardContent>
          </RequirementCard>
          
          <RequirementCard>
            <CardHeader>
              <CardIcon><FiCheckCircle /></CardIcon>
              <CardTitle>Eligibility</CardTitle>
            </CardHeader>
            <CardContent>
              <p>{courseDetail.eligibility}</p>
            </CardContent>
          </RequirementCard>
          
          <RequirementCard>
            <CardHeader>
              <CardIcon><FiClipboard /></CardIcon>
              <CardTitle>Regulatory Requirements</CardTitle>
            </CardHeader>
            <CardContent>
              <p>{courseDetail.regulatoryRequirements}</p>
            </CardContent>
          </RequirementCard>
        </RequirementsGrid>
      </Container>
    </Section>
  );
};

export default RequirementsSection; 